import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  firstRunCompletePatch,
  firstRunDismissPatch,
  useSettings,
  type ColorScheme,
  type Settings,
  type VisualStyle,
} from "@helm/core";
import { allSchemes, colorSchemes } from "@helm/design";
import { applyVisualSettings, settingsSync } from "../session";
import { requestFlattenAll } from "../trading/actions";
import { TEMPLATES } from "./templates";
import { DESCRIPTIONS, ModulePreview } from "./WorkspacePicker";
import "./workspacePicker.css";

/**
 * Trader types (UI-REVAMP §8) over existing template IDs only; `style` is the suggested chrome,
 * pre-selected in the next step until the trader picks a style themselves.
 */
const TRADER_TYPES: ReadonlyArray<{
  id: string;
  label: string;
  template: string | null;
  style: VisualStyle;
}> = [
  { id: "day", label: "Day trader", template: "tradovate", style: "pro" },
  { id: "scalper", label: "Scalper", template: "scalper", style: "terminal" },
  { id: "chart-focus", label: "Chart focus", template: "chart-focus", style: "focus" },
  { id: "multi-timeframe", label: "Multi-timeframe", template: "chart-grid", style: "pro" },
  { id: "order-flow", label: "Order flow", template: "order-flow", style: "terminal" },
  { id: "prop-eval", label: "Prop evaluation", template: "prop-eval", style: "pro" },
  { id: "keep", label: "Keep my current layout", template: null, style: "pro" },
];

/** Same order and names as Settings → Appearance. */
const STYLES: ReadonlyArray<{ id: VisualStyle; label: string; text: string }> = [
  { id: "pro", label: "Pro", text: "Balanced: 24 px rows, 4 px corners." },
  { id: "terminal", label: "Terminal", text: "Dense console: 20 px rows, square, monospace." },
  { id: "focus", label: "Focus", text: "Compact chrome (edge rails come later)." },
  { id: "studio", label: "Studio", text: "Spacious: 28 px rows, larger type." },
  { id: "tradovate", label: "Classic (Tradovate)", text: "The Tradovate look as it is today." },
  { id: "tradesea", label: "Tradesea (original)", text: "TradePrizm's original style." },
  { id: "glacis", label: "Glacis", text: "Near-black, flat and dense." },
];

const SCHEMES: ReadonlyArray<ColorScheme> = [
  ...(Object.keys(colorSchemes) as Array<keyof typeof colorSchemes>),
  "legacy",
];

const STEPS = ["Trader type", "Style", "Colour scheme"] as const;

/** Appearance sample drawn with the live tokens (no prices, accounts or market data). */
function AppearanceSample(): React.JSX.Element {
  return (
    <div className="first-run-sample" aria-hidden="true">
      <div className="first-run-sample-bar">
        <span>Chart</span>
        <span className="first-run-sample-tab">DOM</span>
      </div>
      {[0, 1, 2].map((row) => (
        <div key={row} className="first-run-sample-row">
          <span className="first-run-sample-cell" />
          <span className="first-run-sample-cell short" />
        </div>
      ))}
      <div className="first-run-sample-actions">
        <span className="first-run-sample-buy">Buy</span>
        <span className="first-run-sample-sell">Sell</span>
      </div>
    </div>
  );
}

/**
 * First-run setup (owner 2026-09-30, UI-REVAMP §8): trader type (an existing layout template) →
 * style → colour scheme. Browsing previews the style and scheme live on the whole window; nothing
 * is saved until Finish. Finish stores the choices and adds the chosen layout as a workspace (the
 * current one is kept); "Skip setup" stores the dismissal. Either way no device offers it again.
 * Escape or Cancel just closes it, so the invitation stays.
 */
export function FirstRunSetup({
  activeTemplate,
  canAddWorkspace,
  onAddWorkspace,
  onClose,
}: {
  activeTemplate: string;
  canAddWorkspace: boolean;
  onAddWorkspace: (template: string, name: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const id = useId();
  const settings = useSettings((s) => s.settings);
  const [step, setStep] = useState(0);
  const [trader, setTrader] = useState(TRADER_TYPES[0]?.id ?? "keep");
  const [style, setStyle] = useState<VisualStyle>("pro");
  const [styleTouched, setStyleTouched] = useState(false);
  const [scheme, setScheme] = useState<ColorScheme>("midnight");
  const dialog = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const done = useRef(false);
  const type = TRADER_TYPES.find((t) => t.id === trader);
  const template = TEMPLATES.find((t) => t.id === type?.template);

  // Live preview on the window, re-applied over remote refreshes; the saved look comes back on close.
  useEffect(() => {
    const preview: Settings = {
      ...settings,
      visual_style: style,
      visual_style_chosen: true,
      color_scheme: scheme,
    };
    applyVisualSettings(preview);
  }, [settings, style, scheme]);
  useEffect(
    () => () => {
      applyVisualSettings(useSettings.getState().settings);
    },
    [],
  );

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    return () => {
      const target =
        previous?.isConnected && previous !== document.body
          ? previous
          : document.querySelector<HTMLElement>('button[aria-label="New workspace"]');
      target?.focus();
    };
  }, []);
  // Each step starts on its selected option (arrow keys then move within the group).
  useEffect(() => {
    body.current?.querySelector<HTMLInputElement>("input[type=radio]:checked")?.focus();
  }, [step]);

  const finish = (): void => {
    if (done.current) return;
    done.current = true;
    if (template && template.id !== activeTemplate && canAddWorkspace)
      onAddWorkspace(template.id, template.name);
    void settingsSync.update(firstRunCompletePatch(style, scheme));
    onClose();
  };
  const skip = (): void => {
    if (done.current) return;
    done.current = true;
    void settingsSync.update(firstRunDismissPatch());
    onClose();
  };

  return createPortal(
    <div className="workspace-picker-backdrop">
      <div
        ref={dialog}
        className="workspace-picker first-run-setup"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
          } else if (event.key === "Tab") {
            const items = [
              ...(dialog.current?.querySelectorAll<HTMLElement>(
                "button:not([disabled]), input:not([disabled])",
              ) ?? []),
            ].filter(
              (element) =>
                element.getClientRects().length > 0 &&
                // Radio groups are one tab stop (the checked option).
                !(element instanceof HTMLInputElement && element.type === "radio" && !element.checked),
            );
            const first = items[0];
            const last = items.at(-1);
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          } else if (
            event.ctrlKey &&
            event.shiftKey &&
            !event.altKey &&
            !event.metaKey &&
            event.code === "KeyF"
          ) {
            event.preventDefault();
            onClose();
            requestFlattenAll();
          }
          event.stopPropagation();
        }}
      >
        <header className="workspace-picker-head">
          <h2 id={`${id}-title`}>
            Set up your screen
            <span className="first-run-step">
              Step {step + 1} of {STEPS.length}: {STEPS[step]}
            </span>
          </h2>
          <button type="button" className="btn btn-sm" onClick={skip}>
            Skip setup
          </button>
        </header>
        <ol className="first-run-progress" aria-label="Setup steps">
          {STEPS.map((label, i) => (
            <li key={label} className={i === step ? "current" : i < step ? "done" : ""}>
              {label}
            </li>
          ))}
        </ol>
        <p id={`${id}-description`} className="workspace-picker-description">
          {step === 0
            ? "Pick the layout closest to how you trade. It opens as a new workspace; your current one stays."
            : "The whole window previews your choice. Nothing is saved until Finish."}{" "}
          You can change all of this later in Settings → Appearance and New workspace.
        </p>
        <div ref={body} className="first-run-body">
          {step === 0 && (
            <div className="workspace-presets first-run-options" role="radiogroup" aria-label="Trader type">
              {TRADER_TYPES.map((t) => {
                const tpl = TEMPLATES.find((x) => x.id === t.template);
                if (t.template !== null && !tpl) return null;
                return (
                  <label
                    key={t.id}
                    className={trader === t.id ? "workspace-preset selected" : "workspace-preset"}
                  >
                    <span className="workspace-preset-title">
                      <input
                        type="radio"
                        name={`${id}-trader`}
                        value={t.id}
                        checked={trader === t.id}
                        onChange={() => {
                          setTrader(t.id);
                          if (!styleTouched) setStyle(t.style);
                        }}
                      />
                      {t.label}
                    </span>
                    {tpl ? (
                      <>
                        <ModulePreview template={tpl} />
                        <span className="workspace-preset-description">
                          {tpl.name} layout
                          {tpl.id === activeTemplate ? " (your current workspace)" : ""}.{" "}
                          {DESCRIPTIONS[tpl.id] ?? ""}
                        </span>
                      </>
                    ) : (
                      <span className="workspace-preset-description">No new workspace is added.</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
          {step === 1 && (
            <div className="first-run-split">
              <div className="first-run-options first-run-list" role="radiogroup" aria-label="Style">
                {STYLES.map((s) => (
                  <label
                    key={s.id}
                    className={style === s.id ? "first-run-option selected" : "first-run-option"}
                  >
                    <input
                      type="radio"
                      name={`${id}-style`}
                      value={s.id}
                      checked={style === s.id}
                      onChange={() => {
                        setStyle(s.id);
                        setStyleTouched(true);
                      }}
                    />
                    <span>
                      <span className="first-run-option-label">
                        {s.label}
                        {s.id === type?.style && <span className="first-run-tag">suggested</span>}
                      </span>
                      <span className="first-run-option-text">{s.text}</span>
                    </span>
                  </label>
                ))}
              </div>
              <AppearanceSample />
            </div>
          )}
          {step === 2 && (
            <div className="first-run-split">
              <div className="first-run-options first-run-list" role="radiogroup" aria-label="Colour scheme">
                {SCHEMES.map((s) => {
                  const named = s === "legacy" ? null : allSchemes[s];
                  return (
                    <label
                      key={s}
                      className={scheme === s ? "first-run-option selected" : "first-run-option"}
                    >
                      <input
                        type="radio"
                        name={`${id}-scheme`}
                        value={s}
                        checked={scheme === s}
                        onChange={() => {
                          setScheme(s);
                        }}
                      />
                      <span>
                        <span className="first-run-option-label">
                          {named ? named.label : "Original style palette"}
                        </span>
                        <span className="first-run-option-text">
                          {named
                            ? `${named.mode === "light" ? "Light" : "Dark"} scheme`
                            : "The style's own colours"}
                        </span>
                      </span>
                      {named && (
                        <span className="first-run-swatches" aria-hidden="true">
                          {[...named.surfaces, named.accent].map((c, i) => (
                            <span key={i} style={{ background: c }} />
                          ))}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              <AppearanceSample />
            </div>
          )}
        </div>
        <footer className="workspace-picker-footer first-run-footer">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          <span className="spacer" />
          {step > 0 && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setStep(step - 1);
              }}
            >
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setStep(step + 1);
              }}
            >
              Next
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={finish}>
              Finish
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
